import type { Control } from "../core/Control.js";
import { isPinnable, pin } from "../core/pin.js";
import type { Flare, FlareSpec } from "../flare/Flare.js";
import type { ThemePalette } from "../themes/Theme.js";
import {
  MOTION_CURVES,
  MOTION_LOOPING,
  MOTION_TRIGGERS,
  motionPreset,
  motionPresets,
  type AnimationGroupSpec,
  type MotionChannel,
  type MotionCurve,
  type MotionStep,
  type MotionTrigger,
  type PresetOptions,
} from "../motion/animation.js";
import { MarkupError } from "./errors.js";
import { compileDrawing, outlineOf } from "../art/markup/drawing.js";
import type { AttrTable, Attrs, BuildContext } from "./registry.js";
import { parseColor } from "./values.js";
import type { XmlElement } from "./xml.js";
import { parseVariableValue, VARIABLE_TYPES, type VariableType } from "./variables.js";

/**
 * Child elements every control accepts, because they describe the control rather than add to
 * it: how it moves (`<Motion>`, `<AnimationGroup>`) and what it wears (`<Flare>`).
 *
 * WoW's FrameXML is the precedent - a frame's `<Animations>` and its `<Layers>` are written inside
 * it, beside its children - and it is the shape a visual designer produces: select a control,
 * give it an entrance, and the entrance is part of that control's element.
 */
export type DecorationBuilder = (element: XmlElement, ctx: BuildContext, control: Control) => void;

const MOTION_ATTRS: AttrTable = {
  name: "string",
  entrance: motionPresets("entrance"),
  loop: motionPresets("loop"),
  hover: motionPresets("hover"),
  press: motionPresets("press"),
  start: ["load", "reveal"],
  duration: "number",
  delay: "number",
  amount: "number",
};

const GROUP_ATTRS: AttrTable = {
  name: "string",
  trigger: MOTION_TRIGGERS,
  looping: MOTION_LOOPING,
};

const TIMING: AttrTable = {
  duration: "number",
  delay: "number",
  order: "number",
  curve: MOTION_CURVES,
};

const STEP_ATTRS: Record<string, AttrTable> = {
  Alpha: { from: "number", to: "number", ...TIMING },
  Scale: { from: "number", to: "number", ...TIMING },
  Reveal: { from: "number", to: "number", ...TIMING },
  Translation: { fromX: "number", toX: "number", fromY: "number", toY: "number", ...TIMING },
};

/** What an unwritten `from`/`to` means for each single-channel step: its resting value. */
const REST_OF: Record<string, [MotionChannel, number]> = {
  Alpha: ["alpha", 1],
  Scale: ["scale", 1],
  Reveal: ["reveal", 1],
};

/**
 * `<Motion entrance="rise" delay="0.3"/>` - presets, the one-click layer.
 *
 * One element may name one preset of each kind; the timing attributes apply to all of them, so
 * two entrances wanting different delays are two elements. `start="reveal"` holds an entrance
 * until the control scrolls into view, which is the whole of the landing-page effect.
 */
function motion(element: XmlElement, ctx: BuildContext, control: Control): void {
  refuseChildren(element);
  const attrs = ctx.attributesOf(element, MOTION_ATTRS);
  const options: PresetOptions = {};
  if (typeof attrs["duration"] === "number") options.duration = attrs["duration"];
  if (typeof attrs["delay"] === "number") options.delay = attrs["delay"];
  if (typeof attrs["amount"] === "number") options.amount = attrs["amount"];
  const name = attrs["name"] as string | undefined;

  let added = 0;
  for (const kind of ["entrance", "loop", "hover", "press"] as const) {
    const preset = attrs[kind] as string | undefined;
    if (!preset) continue;
    const group = motionPreset(preset, { ...options, ...(name ? { name } : {}) });
    if (kind === "entrance" && attrs["start"] === "reveal") group.trigger = "reveal";
    control.motion.add(group);
    added++;
  }
  if (added === 0) {
    throw new MarkupError(
      `<Motion> names no preset; give it one of entrance, loop, hover or press`,
      element,
    );
  }
}

/**
 * `<AnimationGroup>` - the long form a preset expands into, written out.
 *
 * ```xml
 * <AnimationGroup name="glowIn" trigger="reveal">
 *   <Alpha from="0" to="1" duration="0.4"/>
 *   <Translation fromY="24" toY="0" duration="0.6" curve="out"/>
 *   <Scale from="1" to="1.05" duration="0.2" order="1"/>
 * </AnimationGroup>
 * ```
 */
function animationGroup(element: XmlElement, ctx: BuildContext, control: Control): void {
  const attrs = ctx.attributesOf(element, GROUP_ATTRS);
  const steps: MotionStep[] = [];
  for (const child of element.children) {
    const table = STEP_ATTRS[child.name];
    if (!table) {
      throw new MarkupError(
        `<${child.name}> cannot go in an <AnimationGroup>; it takes ${Object.keys(STEP_ATTRS).join(", ")}`,
        child,
      );
    }
    refuseChildren(child);
    steps.push(...stepsOf(child.name, ctx.attributesOf(child, table)));
  }
  if (steps.length === 0) {
    throw new MarkupError(`<AnimationGroup> has no steps; it needs at least one of ${Object.keys(STEP_ATTRS).join(", ")}`, element);
  }
  const group: AnimationGroupSpec = {
    trigger: (attrs["trigger"] as MotionTrigger | undefined) ?? "load",
    looping: (attrs["looping"] as AnimationGroupSpec["looping"]) ?? "none",
    steps,
  };
  if (attrs["name"]) group.name = attrs["name"] as string;
  control.motion.add(group);
}

function stepsOf(tag: string, a: Attrs): MotionStep[] {
  const timing = {
    duration: typeof a["duration"] === "number" ? Math.max(0, a["duration"]) : 0.5,
    ...(typeof a["delay"] === "number" ? { delay: a["delay"] } : {}),
    ...(typeof a["order"] === "number" ? { order: a["order"] } : {}),
    ...(a["curve"] ? { curve: a["curve"] as MotionCurve } : {}),
  };
  const rest = REST_OF[tag];
  if (rest) {
    const [channel, value] = rest;
    return [{ channel, from: num(a["from"], value), to: num(a["to"], value), ...timing }];
  }
  // Translation: an axis nobody wrote is an axis that does not move, so it is left out.
  const out: MotionStep[] = [];
  if (a["fromX"] !== undefined || a["toX"] !== undefined) {
    out.push({ channel: "x", from: num(a["fromX"], 0), to: num(a["toX"], 0), ...timing });
  }
  if (a["fromY"] !== undefined || a["toY"] !== undefined) {
    out.push({ channel: "y", from: num(a["fromY"], 0), to: num(a["toY"], 0), ...timing });
  }
  return out;
}

/**
 * `<Flare kind="glow" color="accent" spread="12"/>` - one of the registered effects.
 *
 * A flare's options vary by kind and are read defensively by its factory, so they are not
 * declared here: a number reads as a number, `true`/`false` as a boolean, a palette name or a
 * hex string as a color, anything else as text. Colors are resolved again on a theme change,
 * which is what lets `color="accent"` mean the new theme's accent rather than the old one's.
 */
function flare(element: XmlElement, ctx: BuildContext, control: Control): void {
  refuseChildren(element);
  const kind = element.attributes.find((a) => a.name === "kind")?.value.trim();
  if (!kind) throw new MarkupError(`<Flare> needs a kind, such as kind="glow"`, element);

  let current: Flare | null = null;
  const apply = (palette: ThemePalette): void => {
    const spec: FlareSpec = { kind };
    for (const attribute of element.attributes) {
      if (attribute.name === "kind") continue;
      spec[attribute.name] = flareValue(attribute.value, palette);
    }
    try {
      current = current ? control.flare.replace(current, spec) : control.flare.add(spec);
    } catch (error) {
      throw new MarkupError((error as Error).message.replace(/^webwin: /, ""), element);
    }
  };
  ctx.bindTheme((theme) => apply(theme.palette));
}

function flareValue(text: string, palette: ThemePalette): unknown {
  const s = text.trim();
  if (s === "true") return true;
  if (s === "false") return false;
  if (s !== "" && Number.isFinite(Number(s))) return Number(s);
  return parseColor(s, palette) ?? s;
}

function num(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function refuseChildren(element: XmlElement): void {
  const child = element.children[0];
  if (child) throw new MarkupError(`<${element.name}> takes no child elements`, child);
}

const VARIABLE_ATTRS: AttrTable = {
  name: "string",
  type: VARIABLE_TYPES,
  value: "string",
  ease: "number",
  cycle: "number",
};

/**
 * `<Variables>` - the values a host feeds the document, declared where the document can say
 * what they are.
 *
 * ```xml
 * <Variables>
 *   <Variable name="progress" type="number" value="0" ease="8"/>
 *   <Variable name="fire" type="color" value="#ff5a1f"/>
 *   <Variable name="tip" type="choice" cycle="6">First tip|Second tip|Third tip</Variable>
 * </Variables>
 * ```
 *
 * It may sit inside any element; the variables belong to the whole document either way. A choice's
 * options are its text, split on `|`.
 */
function variables(element: XmlElement, ctx: BuildContext): void {
  for (const child of element.children) {
    if (child.name !== "Variable") {
      throw new MarkupError(`<${child.name}> cannot go in <Variables>; it takes <Variable>`, child);
    }
    refuseChildren(child);
    const a = ctx.attributesOf(child, VARIABLE_ATTRS);
    const name = a["name"] as string | undefined;
    if (!name) throw new MarkupError(`<Variable> needs a name`, child);
    const type = (a["type"] as VariableType | undefined) ?? "text";
    const choices =
      type === "choice"
        ? child.text.split("|").map((s) => s.trim()).filter((s) => s.length > 0)
        : undefined;
    if (type === "choice" && (!choices || choices.length === 0)) {
      throw new MarkupError(`<Variable name="${name}" type="choice"> needs its choices as text: "a|b|c"`, child);
    }
    const written = (a["value"] as string | undefined) ?? (type === "text" ? child.text : "");
    ctx.variables.define(
      {
        name,
        type,
        value: parseVariableValue(type, written),
        ...(typeof a["ease"] === "number" ? { ease: a["ease"] } : {}),
        ...(typeof a["cycle"] === "number" ? { cycle: a["cycle"] } : {}),
        ...(choices ? { choices } : {}),
      },
      child,
    );
  }
}

const LINK_ATTRS: AttrTable = { prop: "string", variable: "string", format: "string", decimals: "number" };

/**
 * `<Link prop="value" variable="progress"/>` - this control's property follows a variable.
 *
 * `format` writes the value into text - `#` is the value, so `format="#%"` - and `decimals` rounds a number
 * first; a format with no `decimals` rounds to a whole number, since a percentage with fifteen
 * places is never what anybody meant.
 */
function link(element: XmlElement, ctx: BuildContext, control: Control): void {
  refuseChildren(element);
  const a = ctx.attributesOf(element, LINK_ATTRS);
  const prop = a["prop"] as string | undefined;
  const variable = a["variable"] as string | undefined;
  if (!prop || !variable) throw new MarkupError(`<Link> needs both prop and variable`, element);
  if (!(prop in control)) {
    throw new MarkupError(`<${control.constructor.name}> has no property "${prop}" to link`, element);
  }
  const format = (a["format"] as string | undefined) ?? null;
  const decimals = typeof a["decimals"] === "number" ? a["decimals"] : format !== null ? 0 : -1;
  ctx.variables.link(variable, control, prop, format, decimals, element);
}

const PIN_ATTRS: AttrTable = {
  target: "string",
  anchor: "string",
  dx: "number",
  dy: "number",
  origin: ["center", "top", "bottom"],
};

/**
 * `<PinTo target="hero" anchor="feet" />` - keep this control on a named point of another, the
 * way a ring stands under a model on a stage. The target is looked up by name once the document
 * is attached, and must be something that can place its points (`Pinnable`).
 */
function pinTo(element: XmlElement, ctx: BuildContext, control: Control): void {
  refuseChildren(element);
  const a = ctx.attributesOf(element, PIN_ATTRS);
  const target = a["target"] as string | undefined;
  const anchor = a["anchor"] as string | undefined;
  if (!target || !anchor) throw new MarkupError(`<PinTo> needs both a target and an anchor`, element);
  // Where it sits is the pin's business now. Left docked - which is what markup gives a control
  // with no placement of its own - the next layout would fill the parent and undo the pin.
  control.dock = "none";
  control.hAlign = null;
  control.vAlign = null;
  ctx.later(() => {
    const found = ctx.view.tryGet(target);
    if (!found) throw new MarkupError(`<PinTo> names "${target}", which is not a control in this document`, element);
    if (!isPinnable(found)) {
      throw new MarkupError(`<${found.constructor.name} name="${target}"> cannot be pinned to; it has no points to name`, element);
    }
    pin(control, found, anchor, {
      ...(typeof a["dx"] === "number" ? { dx: a["dx"] } : {}),
      ...(typeof a["dy"] === "number" ? { dy: a["dy"] } : {}),
      ...(a["origin"] ? { origin: a["origin"] as "center" | "top" | "bottom" } : {}),
    });
  });
}

/**
 * `<Shape>`: the control's outline drawn in art markup, against its own size (`w` and `h`), for a
 * silhouette no preset or path in a 100 box can say - a bevel that stays 12px whatever the width:
 *
 * ```xml
 * <Button text="Enter"><Shape><Polygon points="..."/></Shape></Button>
 * <Panel><Shape><Path d="M12 0 H88 L100 50 L88 100 H12 L0 50 Z"/></Shape></Panel>
 * ```
 *
 * Its filled shapes - `Rect`, `Circle`, `Ellipse`, `Polygon`, `Star`, `Diamond`, a closed `Path` -
 * make the outline; what an expression reads is the control's width and height.
 */
function shape(element: XmlElement, ctx: BuildContext, control: Control): void {
  for (const a of element.attributes) throw new MarkupError(`<Shape> takes no attributes`, a);
  const drawing = compileDrawing(element.children);
  const theme = ctx.theme;
  control.shapeProvider = (w, h) =>
    outlineOf(drawing, { rc: null as never, theme: control.desktop?.theme ?? theme, t: 0, w, h });
}

export const DECORATIONS: Readonly<Record<string, DecorationBuilder>> = {
  Shape: shape,
  Motion: motion,
  AnimationGroup: animationGroup,
  Flare: flare,
  Variables: variables,
  Link: link,
  PinTo: pinTo,
};
